import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, KeyRound, Mail, AlertTriangle, Heart, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ClaimPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState("enter"); // enter | confirm | success
  const [claimCode, setClaimCode] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [claimResult, setClaimResult] = useState(null);
  const [codeStatus, setCodeStatus] = useState(null);

  const handleCheckCode = async () => {
    if (!claimCode.trim()) { toast.error("Enter your claim code"); return; }
    setLoading(true);
    try {
      const res = await api.get(`/claim/status/${claimCode.trim()}`);
      setCodeStatus(res.data);
      if (res.data.is_claimed) {
        toast.info("This account has already been claimed");
      }
      setStep("confirm");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid claim code");
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    if (!email.trim()) { toast.error("Enter your email"); return; }
    setLoading(true);
    try {
      const res = await api.post("/claim/initiate", { claim_code: claimCode.trim(), email: email.trim() });
      setClaimResult(res.data);
      setStep("success");
      toast.success("Account claimed successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Claim failed. Please verify your email matches the beneficiary record.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6" data-testid="claim-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-12">
          <div className="w-10 h-10 rounded-lg bg-[#00FF88] flex items-center justify-center">
            <Shield className="w-5 h-5 text-black" />
          </div>
          <span className="text-xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope' }}>
            Black Box Claim
          </span>
        </div>

        {step === "enter" && (
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-white mb-2" style={{ fontFamily: 'Manrope' }}>
                Account Claim
              </h1>
              <p className="text-sm text-slate-400 leading-relaxed">
                If you've been designated as a beneficiary, enter your claim code to access the account.
                This is a sensitive process that grants you access to a loved one's digital vault.
              </p>
            </div>

            <div className="bg-white/[0.03] backdrop-blur-xl border border-amber-500/20 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
              <p className="text-xs text-slate-400 leading-relaxed">
                Only use this if you are the designated beneficiary and the account holder has passed away.
                Misuse of claim codes is a serious matter.
              </p>
            </div>

            <div className="relative">
              <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Enter claim code (e.g., BB-A1B2C3D4)"
                value={claimCode}
                onChange={(e) => setClaimCode(e.target.value.toUpperCase())}
                data-testid="claim-code-input"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/20 rounded-xl h-14 pl-12 font-mono tracking-wider"
              />
            </div>

            <Button
              onClick={handleCheckCode}
              disabled={loading || !claimCode.trim()}
              data-testid="claim-check-btn"
              className="w-full bg-white text-black hover:bg-slate-200 rounded-full h-14 font-medium"
            >
              {loading ? "Verifying..." : "Verify Code"}
            </Button>

            <p className="text-center text-xs text-slate-600">
              <button onClick={() => navigate("/auth")} className="text-[#00FF88] hover:underline">Sign in</button> to your own account
            </p>
          </motion.div>
        )}

        {step === "confirm" && codeStatus && (
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-white mb-2" style={{ fontFamily: 'Manrope' }}>
                Confirm Your Identity
              </h1>
              <p className="text-sm text-slate-400">
                This code is associated with <strong className="text-white">{codeStatus.owner_name}</strong>'s account.
                Beneficiary: <strong className="text-white">{codeStatus.beneficiary_name}</strong>.
              </p>
            </div>

            {codeStatus.is_claimed ? (
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-6 text-center">
                <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                <p className="text-sm text-emerald-300">This account has already been claimed.</p>
                <p className="text-xs text-slate-400 mt-2">Log in to access the vault if you have admin rights.</p>
                <Button onClick={() => navigate("/auth")} className="mt-4 bg-white text-black rounded-full">Sign In</Button>
              </div>
            ) : (
              <>
                <p className="text-xs text-slate-500">Enter the email address associated with your beneficiary record to confirm your identity.</p>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="claim-email-input"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/20 rounded-xl h-14 pl-12"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setStep("enter")}
                    className="border-white/10 text-slate-400 rounded-full flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleClaim}
                    disabled={loading || !email.trim()}
                    data-testid="claim-submit-btn"
                    className="bg-[#00FF88] text-black hover:bg-[#00CC6A] rounded-full flex-1 font-bold"
                  >
                    {loading ? "Claiming..." : "Claim Account"}
                  </Button>
                </div>
              </>
            )}
          </motion.div>
        )}

        {step === "success" && claimResult && (
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-[#00FF88]/10 flex items-center justify-center mx-auto">
              <Heart className="w-8 h-8 text-[#00FF88]" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope' }}>
              Account Claimed
            </h1>
            <p className="text-sm text-slate-400 leading-relaxed">
              {claimResult.owner_name}'s vault is now accessible.
              {claimResult.legacy_count > 0 && ` ${claimResult.legacy_count} legacy messages have been released and sent to their recipients.`}
            </p>
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-xl p-6">
              <p className="text-xs text-slate-500 mb-3">What happens next:</p>
              <ul className="text-sm text-slate-300 space-y-2 text-left">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> Sign in or create an account with your beneficiary email</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> You'll see an Admin Panel to access the vault</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> Legacy messages have been sent to their intended recipients</li>
              </ul>
            </div>
            <Button
              onClick={() => navigate("/auth")}
              data-testid="claim-sign-in-btn"
              className="bg-white text-black hover:bg-slate-200 rounded-full px-8 py-6 font-medium"
            >
              Sign In to Access Vault
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
